import type { CityConfig } from "@/pages/city-template/types";

export const corvallisConfig: CityConfig = {
  city: "Corvallis",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "corvallis",
  route: "/corvallis",
  title: "Emergency Passport Corvallis, OR | Fast Passport Renewal & Expedited Services",
  metaDescription: "Emergency passport and expedited passport services in Corvallis, OR — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for OSU students and Willamette Valley travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Corvallis, Oregon",
  heroSubtitle: "Passport expired before your study-abroad trip? Flying out of Eugene or PDX in days? Fast Passport Center helps Corvallis travelers get their passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Corvallis%20Oregon%20aerial%20view%20with%20Oregon%20State%20University%20campus%2C%20Willamette%20River%20flowing%20through%20lush%20green%20Pacific%20Northwest%20valley%2C%20rolling%20farmland%20and%20distant%20Coast%20Range%20mountains%2C%20warm%20golden%20afternoon%20light%2C%20peaceful%20college%20town%20atmosphere%2C%20photorealistic%20landscape%20photography%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=corvallis-hero-01&orientation=landscape",
  heroImageAlt: "Corvallis Oregon aerial view with Oregon State University and Willamette River",
  geoPosition: "44.5646;-123.2620",
  geoPlacename: "Corvallis, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 190 miles north — 3.5 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Corvallis", zips: "97330, 97331, 97333" },
    { name: "Albany", zips: "97321–97322" },
    { name: "Philomath", zips: "97370" },
    { name: "Lebanon", zips: "97355" },
    { name: "Dallas", zips: "97338" },
    { name: "Monmouth", zips: "97361" },
    { name: "Independence", zips: "97351" },
    { name: "Newport", zips: "97365" },
    { name: "Lincoln City", zips: "97367" },
    { name: "Toledo", zips: "97391" },
    { name: "Siletz", zips: "97380" },
    { name: "Waldport", zips: "97394" },
    { name: "Sweet Home", zips: "97386" },
    { name: "Tangent", zips: "97389" },
    { name: "Harrisburg", zips: "97446" },
    { name: "Junction City", zips: "97448" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from your dorm, apartment, or office in Corvallis in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Corvallis", desc: "FedEx and UPS locations throughout Corvallis and Albany offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays — perfect for busy students and faculty." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport agency — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Elena R.",
      location: "Corvallis, OR",
      rating: 5,
      text: "OSU study abroad to Spain in three weeks and my passport expired. Fast Passport Center saved the trip — renewed in 7 days and I shipped from the FedEx on 9th Street. So easy.",
      service: "Passport Renewal",
    },
    {
      name: "Dr. Paul K.",
      location: "Corvallis, OR",
      rating: 5,
      text: "Conference in Tokyo announced last minute. Fast Passport Center had my passport expedited in 5 business days. The document review caught a photo issue before submission — that's why I use them.",
      service: "Emergency Passport",
    },
    {
      name: "Maya S.",
      location: "Albany, OR",
      rating: 5,
      text: "My daughter needed a passport for a summer exchange program in France. We live in Albany and the Corvallis area. Fast Passport Center made the child passport process simple — clear checklist, fast turnaround.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "corvallis-main",
      name: "Corvallis Main Post Office",
      type: "USPS Passport Acceptance",
      address: "311 SW 2nd St",
      city: "Corvallis",
      state: "OR",
      zip: "97333",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm, Sat 9am–12pm",
      distance: "Downtown Corvallis",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Corvallis. Appointment required — book online in advance. Photo services may be available.",
      icon: "ri-mail-line",
    },
    {
      id: "corvallis-south",
      name: "Corvallis South Post Office",
      type: "USPS Passport Acceptance",
      address: "1155 SW Cypress St",
      city: "Corvallis",
      state: "OR",
      zip: "97333",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "South Corvallis",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "South Corvallis passport acceptance option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "albany-post",
      name: "Albany Post Office",
      type: "USPS Passport Acceptance",
      address: "525 NW 2nd Ave",
      city: "Albany",
      state: "OR",
      zip: "97321",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm, Sat 9am–12pm",
      distance: "Albany — about 12 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Linn County option for Albany and north Corvallis area residents.",
      icon: "ri-mail-line",
    },
    {
      id: "benton-county",
      name: "Benton County Clerk",
      type: "County Passport Acceptance",
      address: "120 NW 4th St, Suite 13",
      city: "Corvallis",
      state: "OR",
      zip: "97330",
      phone: "541-766-6831",
      hours: "Mon–Fri 8am–5pm (by appointment)",
      distance: "Downtown Corvallis — Courthouse",
      website: "https://www.co.benton.or.us/clerk",
      websiteLabel: "Benton County Clerk",
      notes: "Benton County clerk passport acceptance. Appointment required — may offer faster scheduling than USPS. Call to confirm passport services.",
      icon: "ri-government-line",
    },
    {
      id: "newport-post",
      name: "Newport Post Office",
      type: "USPS Passport Acceptance",
      address: "35 SW 1st St",
      city: "Newport",
      state: "OR",
      zip: "97365",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Newport — about 52 miles west (coastal option)",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Lincoln County option for coastal residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "philomath-post",
      name: "Philomath Post Office",
      type: "USPS Passport Acceptance",
      address: "1565 Main St",
      city: "Philomath",
      state: "OR",
      zip: "97370",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Philomath — about 8 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Philomath / west Corvallis area option. Appointment required.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Corvallis before international travel?",
      a: "Yes. Through Fast Passport Center, Corvallis travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today — ideal for OSU study abroad and Willamette Valley travel plans.",
    },
    {
      q: "How fast can I renew my passport in Corvallis?",
      a: "Fast passport renewal in Corvallis is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years. No office visit required for most renewals.",
    },
    {
      q: "What if my passport expires before my study abroad trip?",
      a: "If your passport expires before a study abroad trip, don't wait. Most programs require your passport to be valid for at least 6 months beyond your return date. Fast Passport Center can expedite your renewal so you don't miss your program start. Start as soon as you receive your acceptance letter.",
    },
    {
      q: "Can I get a same-day passport appointment near Corvallis?",
      a: "Same-day passport appointments at the regional passport agency in Seattle are only available for documented life-or-death emergencies and are not guaranteed. For most urgent passport situations near Corvallis, Fast Passport Center is the faster and more predictable option — no Seattle drive required.",
    },
    {
      q: "Can I expedite a child passport in Corvallis?",
      a: "Yes. Child passport expediting in Corvallis is possible through Fast Passport Center, but child passports (for minors 15 and under) do require an in-person acceptance step with parental consent. We provide a clear checklist and instructions. Once the acceptance step is complete, we handle the expedited processing.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If your passport is lost or stolen before an upcoming trip, act immediately. You will need to complete Form DS-64 to report it lost or stolen and apply for a replacement. Fast Passport Center guides you through this process and can expedite your replacement passport. Don't delay — lost passport replacements take extra steps.",
    },
    {
      q: "Does Corvallis have a passport agency?",
      a: "Corvallis, Oregon does not have a regional passport agency. The closest regional agencies are the Seattle Passport Agency in Washington (about 190 miles north) and the San Francisco Passport Agency in California (about 560 miles south). Both require appointments and proof of travel within 14 days. Fast Passport Center is the faster and more convenient solution for most Corvallis residents.",
    },
    {
      q: "How long does a standard passport take in Corvallis?",
      a: "If you apply through a standard post office acceptance facility in Corvallis, processing times run 6–13 weeks — sometimes longer during peak travel season. If your travel is sooner than that, you need expedited processing. Fast Passport Center offers Corvallis travelers processing as fast as 24 hours.",
    },
    {
      q: "Can I get a passport renewal near me in Corvallis without visiting an office?",
      a: "Yes. Most Corvallis-area adults who are renewing a passport can do so entirely by mail — no in-person visit required. Start your order online with Fast Passport Center, complete your documents at home, and ship them overnight from any FedEx or UPS location in Corvallis or Albany.",
    },
    {
      q: "What airports do Corvallis travelers typically fly from?",
      a: "Corvallis travelers typically use Eugene Airport (EUG) — about 37 miles south — for regional flights and Portland International Airport (PDX) — about 95 miles north — for international departures. Newport and Lincoln City are coastal options. If your passport isn't in hand, don't book international travel. Fast Passport Center can help you meet your deadline.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Corvallis", address: "1905 NW 9th St, Corvallis, OR 97330", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/photo" },
    { name: "CVS — Corvallis", address: "2005 NW Monroe Ave, Corvallis, OR 97330", hours: "Daily 7 AM – 10 PM", website: "https://www.cvs.com/photo" },
    { name: "FedEx Office — Corvallis", address: "1700 NW 9th St, Corvallis, OR 97330", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 8 PM", website: "https://www.office.fedex.com/" },
    { name: "The UPS Store — Corvallis", address: "1955 NW 9th St, Corvallis, OR 97330", hours: "Mon–Fri 8 AM – 7 PM", website: "https://www.theupsstore.com/" },
    { name: "Walmart — Corvallis", address: "1815 NW 9th St, Corvallis, OR 97330", hours: "Daily 7 AM – 11 PM", website: "https://www.walmart.com/photo" },
    { name: "Walgreens — Albany", address: "1315 Geary St SE, Albany, OR 97322", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/photo" },
    { name: "CVS — Albany", address: "2250 Santiam Hwy SE, Albany, OR 97322", hours: "Daily 7 AM – 10 PM", website: "https://www.cvs.com/photo" },
    { name: "FedEx Office — Albany", address: "2232 Santiam Hwy SE, Albany, OR 97322", hours: "Mon–Fri 7 AM – 10 PM", website: "https://www.office.fedex.com/" },
    { name: "Walmart — Albany", address: "1330 Goldfish Farm Rd SE, Albany, OR 97322", hours: "Daily 7 AM – 11 PM", website: "https://www.walmart.com/photo" },
    { name: "Walgreens — Newport", address: "145 SW Hurbert St, Newport, OR 97365", hours: "Daily 8 AM – 9 PM", website: "https://www.walgreens.com/photo" },
  ],
  dmvLocations: [
    {
      name: "Corvallis DMV Office",
      address: "1735 NW 9th St, Corvallis, OR 97330",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Benton County DMV office. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Albany DMV Office",
      address: "2250 Santiam Hwy SE, Albany, OR 97322",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Linn County option for Albany and north Corvallis area residents.",
      featured: false,
    },
    {
      name: "Newport DMV Office",
      address: "318 NE 2nd St, Newport, OR 97365",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Lincoln County option for coastal residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "EUG",
      name: "Eugene Airport",
      distance: "Closest regional airport — about 37 miles south",
      address: "28801 Douglas Dr, Eugene, OR 97402",
      note: "EUG offers regional flights and some international connections. Portland International Airport (PDX) is about 95 miles north for more international options.",
      flightUrl: "https://flyeug.com/",
      flightLabel: "EUG Airport Website",
      featured: true,
    },
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Major international hub — about 95 miles north",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "PDX offers direct international flights to Europe, Asia, and Canada. The busiest travel seasons are May–August and December–January.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: false,
    },
    {
      code: "ONP",
      name: "Newport Municipal Airport",
      distance: "General aviation — about 52 miles west",
      address: "135 SE 84th St, Newport, OR 97366",
      note: "General aviation only. No commercial flights. International travelers use PDX or EUG.",
      flightUrl: "https://www.newportor.gov/",
      flightLabel: "Newport Airport Info",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from EUG or PDX in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Corvallis and the Willamette Valley.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Corvallis and Willamette Valley travelers renew fast — perfect for students, faculty, and residents.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Corvallis", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Corvallis travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Corvallis families — minors under 16 require in-person acceptance and parental consent. We provide clear instructions and checklists.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Corvallis residents — we guide you through the acceptance clerk process and handle expedited processing.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines. Same-day passport help for Corvallis travelers.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all handled with the same expedited courier access.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Corvallis, Oregon travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Corvallis residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Corvallis, OR residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Corvallis travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Corvallis families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving Corvallis, Oregon." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Corvallis students and travelers often assume 6–8 weeks is safe — it's not. Government processing has stretched to 10–14+ weeks during peak travel season. Apply as soon as you book your trip, especially for study abroad and summer travel." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from campus photo booths and drugstores often get rejected for lighting, background, or expression issues. We provide exact specs and can catch these issues before your documents are submitted." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission, critical for last-minute passport renewal in Corvallis." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. Fast Passport Center tells you exactly what you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing out of Corvallis runs 6–13 weeks. If your trip to Europe, Japan, or Canada is in under 10 weeks, standard processing is a gamble. Use our courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly — especially for international flights out of PDX or EUG. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Corvallis travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Corvallis", description: "Drop off at any FedEx or UPS in the Corvallis area — most locations are open evenings. No office visit to our facility required." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system entirely." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping." },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (3.5 hours each way)", fpc: "Ship documents overnight from Corvallis" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Portland", route: "/portland" },
    { name: "Fast passport renewal in Eugene", route: "/eugene" },
    { name: "Emergency passport help near Salem", route: "/salem" },
    { name: "Rush passport service in Albany", route: "#" },
    { name: "Fast passport renewal for Willamette Valley travelers", route: "/oregon" },
  ],
  fpcOfficeAddress: "5608 17th Avenue NW, Seattle, WA 98107",
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Corvallis or Albany — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Corvallis and the entire Willamette Valley remotely",
  serviceAreaIntro: "Whether you are in downtown Corvallis, near Oregon State University, Albany, Philomath, or anywhere in Benton and Linn Counties, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Corvallis-area zip codes. Anywhere in Benton, Linn, or western Lane County — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Corvallis as simple as possible. Most Corvallis clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Corvallis travelers. Fast Passport Center reviews your documents before submission to catch these issues before they become problems.",
  testimonialsIntro: "Real reviews from Corvallis, Albany, and Willamette Valley travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Corvallis and Willamette Valley travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  dmvWebsite: "https://www.oregon.gov/odot/dmv",
  dmvLabel: "Oregon DMV — Official Website",
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Oregon Vital Records (Birth, Death, Marriage)", desc: "Request certified birth, death, and marriage certificates from Oregon Vital Records.", url: "https://www.oregon.gov/oha/PH/BirthDeathCertificates/Pages/index.aspx" },
    { icon: "ri-government-line", label: "Benton County Clerk Vital Records", desc: "Obtain vital records through the Benton County Clerk's office in Corvallis.", url: "https://www.co.benton.or.us/clerk" },
    { icon: "ri-global-line", label: "VitalChek — Order Vital Records Online", desc: "Official online ordering partner of Oregon Vital Records for certified certificates.", url: "https://www.vitalchek.com/" },
  ],
};